import { Link } from "react-router-dom";
import { ArrowRight, Download } from "lucide-react";
import { SiteLayout, PageHeader } from "../layouts/SiteLayout";
import { ASSETS, SPEAKING_TOPICS, PRESS_KIT_BIOS, BRAND } from "../data/siteContent";
import { SPEAKING_T } from "../constants/siteTestIds";
import { Seo } from "../seo/Seo";
import { buildSchemas, webPageSchema, breadcrumbSchema } from "../seo/schemas";

export default function Speaking() {
  return (
    <SiteLayout>
      <Seo
        title="Book Manuel Santana to Speak — Keynotes, Fireside Chats, Panels"
        description="Manuel Santana delivers keynotes, fireside chats, panels and workshops on modern relationships, gender dynamics, media and identity. Available globally in English & Spanish."
        path="/speaking"
        schemas={buildSchemas([
          webPageSchema({ path: "/speaking", name: "Book Manuel Santana to Speak", description: "Speaking topics, bio and booking information." }),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Speaking", path: "/speaking" }]),
        ])}
      />
      <PageHeader
        overline="Speaking — Booking"
        title="Bring Manuel"
        italic="to your stage."
        kicker="Keynotes, fireside chats, panel hosting and live event moderation. Delivered in English & Spanish, across the United States and beyond."
      />

      <section data-testid={SPEAKING_T.section} className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid grid-cols-12 gap-8 lg:gap-16">
          <aside className="col-span-12 lg:col-span-5">
            <div className="portrait-frame w-full max-w-[560px] aspect-[4/5]">
              <img src={ASSETS.bookingPortrait} alt="Manuel Santana — speaker headshot" draggable={false} />
            </div>
            <p className="mt-5 font-mono-ui text-[10px] tracking-[0.3em] uppercase text-black/40">— Speaker headshot · Available for press use</p>

            <div className="mt-10 border border-black/15 p-6">
              <p className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[#0a0a0a]">— Speaker One-Sheet</p>
              <p className="mt-4 text-black/65 text-sm leading-relaxed">
                Bio, topics, tech rider, AV requirements and audience demographics — request the full speaker one-sheet and recent video reel.
              </p>
              <Link to="/contact" className="mt-6 inline-flex btn-ghost">
                <Download size={16} /> Request One-Sheet
              </Link>
            </div>
          </aside>

          <div className="col-span-12 lg:col-span-7 space-y-14">
            <div data-testid={SPEAKING_T.topics}>
              <p className="font-mono-ui text-[11px] tracking-[0.3em] uppercase text-[#0a0a0a]">— Signature Topics</p>
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
                {SPEAKING_TOPICS.map((t, i) => (
                  <div key={t.title}>
                    <span className="font-mono-ui text-[11px] tracking-[0.3em] text-black/40">№ 0{i + 1}</span>
                    <h3 className="mt-3 font-display text-[#0a0a0a] text-2xl md:text-3xl tracking-tight leading-tight">{t.title}</h3>
                    <p className="mt-4 text-black/60 text-sm leading-relaxed">{t.body}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="font-mono-ui text-[11px] tracking-[0.3em] uppercase text-[#0a0a0a]">— Short Speaker Bio</p>
              <p className="mt-6 text-black/75 text-base md:text-lg leading-relaxed">{PRESS_KIT_BIOS.medium}</p>
            </div>

            <div>
              <p className="font-mono-ui text-[11px] tracking-[0.3em] uppercase text-[#0a0a0a]">— Booking Information</p>
              <ul className="mt-6 text-black/70 text-base leading-relaxed space-y-3">
                <li><span className="font-mono-ui text-[11px] tracking-[0.2em] uppercase text-black/45 mr-3">Format</span> Keynote · Fireside · Panel · Workshop</li>
                <li><span className="font-mono-ui text-[11px] tracking-[0.2em] uppercase text-black/45 mr-3">Languages</span> English & Spanish</li>
                <li><span className="font-mono-ui text-[11px] tracking-[0.2em] uppercase text-black/45 mr-3">Travel</span> Based between {BRAND.cities.join(", ")} — available nationally & internationally</li>
                <li><span className="font-mono-ui text-[11px] tracking-[0.2em] uppercase text-black/45 mr-3">Audiences</span> Corporate · Conference · University · Private</li>
                <li><span className="font-mono-ui text-[11px] tracking-[0.2em] uppercase text-black/45 mr-3">Response</span> Under 48 hours</li>
              </ul>
              <Link to="/contact" className="mt-10 inline-flex btn-primary">
                Send A Booking Inquiry <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
